import { useCallback, useEffect, useRef, useState } from "react";
import { useDrillStore } from "@/application/stores/drillStore";
import { useEditorStore } from "@/application/stores/editorStore";
import { judge } from "@/domain/learning/judge";
import { keystrokesUsed } from "@/domain/learning/keystroke-count";
import type { Drill } from "@/domain/learning/model/drill";
import type { CommandSet } from "@/domain/vim/model/command-set";
import { createEditorState } from "@/domain/vim/model/editor-state";
import {
  isModifierOnlyKey,
  keystrokeNotation,
  normalizeKeystroke,
  type RawKeyInput,
} from "@/domain/vim/model/keystroke";
import { reduce } from "@/domain/vim/reduce";
import {
  classifyKeystrokeOutcome,
  type KeystrokeOutcome,
  modifierOnlyLabel,
  pushStripFrame,
  type StripFrame,
} from "@/presentation/features/drill/keystroke-log";

export interface UseDrillSessionResult {
  readonly strip: readonly StripFrame[];
  readonly handleKeydown: (input: RawKeyInput) => void;
  readonly reset: () => void;
}

/**
 * 1つの演習（`Drill`）に対する「打鍵 → vimエンジン → 判定 → 打鍵の帯」の一連を束ねるフック。
 * `editorStore` / `drillStore` を更新する副作用を持つ。両ストアとも、利用側コンポーネントは
 * このフックの戻り値ではなくストア自身をセレクタで購読すること（NFR-002のための規則。
 * `editorStore.ts` のコメント参照）。このフックが返す `strip` はvimエンジンの外側にある
 * 純粋な表示用ログであり、ストアには置かない（打鍵の帯は演習単位の一時的な表示情報のため）。
 *
 * `onOutcome`（計画13で追加）: 1打ごとの分類結果をそのまま呼び出し側へ通知する任意コールバック。
 * 師範代の「未習得キー」反応・打ち音（技が確定した瞬間のみ）・無操作タイマーのリセットは、
 * いずれもこの1打単位の情報が必要なため、`useDrillSession`自身には持たせず外側へ委譲する。
 */
export function useDrillSession(
  drill: Drill,
  commandSet: CommandSet,
  onOutcome?: (outcome: KeystrokeOutcome) => void,
): UseDrillSessionResult {
  const setEditorState = useEditorStore((state) => state.setEditorState);
  const setCurrentDrillId = useDrillStore((state) => state.setCurrentDrillId);
  const setVerdict = useDrillStore((state) => state.setVerdict);
  const setRevealedHintCount = useDrillStore((state) => state.setRevealedHintCount);
  const [strip, setStrip] = useState<readonly StripFrame[]>([]);
  /**
   * この試行で受理された打鍵の表記列（未習・修飾キー単独は含まない）。
   * `judge()` の `requiredCommands`（学んだ技を実際に使ったか）の照合にだけ使うため、
   * 描画には関与させず ref に持つ（1打ごとの再描画を増やさない — NFR-002）。
   */
  const executedKeysRef = useRef<string[]>([]);

  const initialize = useCallback(() => {
    setEditorState(createEditorState(drill.initialBuffer, drill.initialCursor));
    setCurrentDrillId(drill.id);
    setVerdict(null);
    setStrip([]);
    executedKeysRef.current = [];
  }, [
    drill.id,
    drill.initialBuffer,
    drill.initialCursor,
    setEditorState,
    setCurrentDrillId,
    setVerdict,
  ]);

  // 演習が変わった時（マウント含む）だけ助言の開示数も0へ戻す。`reset()`（⌫の「もう一度」）は
  // `initialize`を直接呼ぶだけでこのエフェクトを経由しないため、開示済みの助言は保たれる
  // （F-007「助言はペナルティなし」— やり直しても既読の助言を失わない）。
  useEffect(() => {
    initialize();
    setRevealedHintCount(0);
  }, [initialize, setRevealedHintCount]);

  const handleKeydown = useCallback(
    (input: RawKeyInput) => {
      const keystroke = normalizeKeystroke(input);
      if (keystroke === null) {
        if (!isModifierOnlyKey(input.key)) {
          return;
        }
        const outcome: KeystrokeOutcome = {
          kind: "modifier",
          label: modifierOnlyLabel(input.key),
        };
        setStrip((current) => pushStripFrame(current, outcome));
        onOutcome?.(outcome);
        return;
      }

      const before = useEditorStore.getState().editorState;
      if (before === null) {
        return;
      }
      const after = reduce(before, keystroke, commandSet);
      setEditorState(after);
      const outcome = classifyKeystrokeOutcome(before, after, keystroke);
      setStrip((current) => pushStripFrame(current, outcome));
      onOutcome?.(outcome);

      if (outcome.kind !== "unlearned") {
        executedKeysRef.current = [...executedKeysRef.current, keystrokeNotation(keystroke)];
      }

      const verdict = judge(
        drill.goal,
        after,
        keystrokesUsed(after),
        drill.keystrokeLimit,
        executedKeysRef.current,
      );
      setVerdict(verdict);
    },
    [commandSet, drill.goal, drill.keystrokeLimit, setEditorState, setVerdict, onOutcome],
  );

  return { strip, handleKeydown, reset: initialize };
}
